import { isAxiosError } from 'axios';
import moment from 'moment';

import {
  AFTERNOON_TIME_AFTER,
  MORNING_TIME_BEFORE,
  isSplitDay,
} from '@/components/ui/daisy/templates/week-calendar/weekColumns';
import {
  ScheduledLessonFilters,
  listScheduledLessons,
} from '@/data/services/scheduledLessonsServices';
import JsonApiResponse, { Links, Meta } from '@/models/JsonApiResponse';
import ScheduledLesson from '@/models/ScheduledLesson';

export type ScheduledLessonWeekColumn = {
  id: number;
  scheduledLessons: ScheduledLesson[];
};

export type ScheduledLessonsWeekFilters = ScheduledLessonFilters & {
  dateRangeAfter: string;
  dateRangeBefore: string;
};

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

const emptyLinks: Links = { first: '', last: '', next: '', prev: null };

const dayPagesHint = new Map<string, number>();

const toHintKey = (params: ScheduledLessonFilters) =>
  JSON.stringify({ ...params, pageNumber: undefined });

const emptyDayResponse = (
  pageNumber: number,
): JsonApiResponse<ScheduledLesson> => ({
  results: [],
  links: emptyLinks,
  meta: { pagination: { count: 0, page: pageNumber, pages: 0 } },
});

const listDayKeysBetween = (
  dateRangeAfter: string,
  dateRangeBefore: string,
) => {
  const dayKeys: string[] = [];
  const current = moment(dateRangeAfter, DAY_KEY_FORMAT);
  const last = moment(dateRangeBefore, DAY_KEY_FORMAT);

  while (current.isSameOrBefore(last)) {
    dayKeys.push(current.format(DAY_KEY_FORMAT));
    current.add(1, 'day');
  }

  return dayKeys;
};

type WeekColumnParams = Pick<
  ScheduledLessonFilters,
  'date' | 'timeAfter' | 'timeBefore'
>;

const listWeekColumns = (
  dateRangeAfter: string,
  dateRangeBefore: string,
): WeekColumnParams[] =>
  listDayKeysBetween(dateRangeAfter, dateRangeBefore).flatMap(date =>
    isSplitDay(moment(date, DAY_KEY_FORMAT).toDate())
      ? [
          { date, timeBefore: MORNING_TIME_BEFORE },
          { date, timeAfter: AFTERNOON_TIME_AFTER },
        ]
      : [{ date }],
  );

const listDayScheduledLessons = async (params: ScheduledLessonFilters) => {
  const pageNumber = params.pageNumber ?? 1;
  const hintKey = toHintKey(params);
  const knownPages = dayPagesHint.get(hintKey);

  if (knownPages !== undefined && pageNumber > knownPages) {
    return emptyDayResponse(pageNumber);
  }

  try {
    const response = await listScheduledLessons(params);
    dayPagesHint.set(hintKey, response.meta.pagination.pages);
    return response;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return emptyDayResponse(pageNumber);
    }
    throw error;
  }
};

const compileWeekMeta = (
  responses: JsonApiResponse<ScheduledLesson>[],
  pageNumber: number,
): Meta => ({
  pagination: {
    count: responses.reduce(
      (total, { meta }) => total + meta.pagination.count,
      0,
    ),
    page: pageNumber,
    pages: Math.max(...responses.map(({ meta }) => meta.pagination.pages), 0),
  },
});

const compileWeekLinks = (responses: JsonApiResponse<ScheduledLesson>[]) =>
  responses.reduce(
    (deepest, response) =>
      response.meta.pagination.pages > deepest.meta.pagination.pages
        ? response
        : deepest,
    responses[0],
  )?.links ?? emptyLinks;

export const listScheduledLessonsWeek = async (
  params: ScheduledLessonsWeekFilters,
): Promise<JsonApiResponse<ScheduledLessonWeekColumn>> => {
  const { dateRangeAfter, dateRangeBefore, ...filters } = params;
  const columns = listWeekColumns(dateRangeAfter, dateRangeBefore);

  const responses = await Promise.all(
    columns.map(column => listDayScheduledLessons({ ...filters, ...column })),
  );

  return {
    results: responses.map((response, index) => ({
      id: index,
      scheduledLessons: response.results,
    })),
    links: compileWeekLinks(responses),
    meta: compileWeekMeta(responses, params.pageNumber ?? 1),
  };
};
